import React, { useEffect, useState } from "react";
import { IoWarningOutline } from "react-icons/io5";
import { LuFileBadge } from "react-icons/lu";
import { useNavigate } from "react-router-dom";

import Form from "../../../components/Form/Form";
import FormTitle from "../../../components/FormTitle/FormTitle";
import Input from "../../../components/Input/Input";
import Loader from "../../../components/Loader/Loader";
import ModalComponent from "../../../components/Modal/ModalComponent";
import ToggleButton from "../../../components/ToggleButton/ToggleButton";
import TokenGenerator from "../../../components/TokenGenerator/TokenGenerator";
import ClientLicenceService from "../../../services/ClientLicenceService";
import TextAreaInput from "../../../components/TextAreaInput/TextAreaInput";
import { formatMoney, parseMoney } from "../../../utils/number";
import { PriceInput } from "../../../components/PriceInput/PriceInput";
import { OrganizationSelector } from "../../../components/OrganizationSelector/OrganizationSelector";
import { UnitSelector } from "../../../components/UnitSelector/UnitSelector";
import { LicenseTypeSelector } from "../../../components/LicenseTypeSelector/LicenseTypeSelector";
import { LicenseFeaturesSelector } from "../../../components/LicenseFeatureSelector/LicenseFeatureSelector";
import { ExpandableSection } from "../../../components/ExpandableSection/ExpandableSection";
import { BillingPeriodSelector } from "../../../components/BillingPeriodSelector/BillingPeriodSelector";
import LicenseService from "../../../services/LicensesService";
import { licenseSchema } from "../../../validations/new-license.schema";
import { DiscountOnAutomaticReadjustmentText, DiscountType, PaymentMethodText } from "../../../constants/billing";
import { DiscountReasonSelector } from "../../../components/DiscountReasonSelector/DiscountReasonSelector";
import Select from "../../../components/Select/Select";

function FormLicense(props) {
   const [tag, setTag] = useState("");
   const [bindingCode, setBindingCode] = useState("");
   const [initialOwnerUnitId, setInitialOwnerUnitId] = useState("");
   const [organizationId, setOrganizationId] = useState("");
   const [serialNumber, setSerialNumber] = useState("");
   const [tokenMqtt, setTokenMqtt] = useState("");
   const [startDate, setStartDate] = useState("");
   const [initialDueDate, setInitialDueDate] = useState("");
   const [description, setDescription] = useState("");
   const [automaticRenew, setAutomaticRenew] = useState(false);
   const [automaticReadjustment, setAutomaticReadjustment] = useState(false);
   const [discountOnAutomaticReadjustment, setDiscountOnAutomaticReadjustment] = useState(null);
   const [type, setType] = useState("");
   const [features, setFeatures] = useState([]);
   const [typeCode, setTypeCode] = useState("");
   const [featureCodes, setFeatureCodes] = useState([]);
   const [period, setPeriod] = useState("");
   const [amount, setAmount] = useState(0);
   const [metadata, setMetadata] = useState({});

   const [isSubmitting, setIsSubmitting] = useState(false);
   const [showModalConfirmNewToken, setShowModalConfirmNewToken] = useState(false);
   const [showLoader, setShowLoader] = useState(false);

   const [isRecalculatingTotalAmount, setIsRecalculatingTotalAmount] = useState(false);
   const [totalAmount, setTotalAmount] = useState(0);
   const [discountReason, setDiscountReason] = useState(null);
   const [isStartPaid, setIsStartPaid] = useState(false);
   const [paymentMethod, setPaymentMethod] = useState(null);

   const [errors, setErrors] = useState({});

   const navigate = useNavigate();

   useEffect(() => {
      if (!props.license) {
         setTag("");
         setBindingCode("");
         setInitialOwnerUnitId("");
         setOrganizationId("");
         setSerialNumber("");
         setTokenMqtt("");
         setStartDate("");
         setInitialDueDate("");
         setDescription("");
         setAutomaticRenew(false);
         setDiscountOnAutomaticReadjustment(null);
         setType("");
         setTypeCode("");
         setFeatureCodes([]);
         setFeatures([]);
         setPeriod("");
         setAmount(0);
         setTotalAmount(0);
         setDiscountReason(null);
         setMetadata({});
         setShowLoader(false);
         setIsStartPaid(false);
         setPaymentMethod(null);
         return;
      }

      const license = props.license;

      setTag(license.tag || "");
      setBindingCode(license.bindingCode || "");
      setInitialOwnerUnitId(license.initialOwnerUnitId || "");
      setOrganizationId(license.organizationId || "");
      setSerialNumber(license.serialNumber || "");
      setTokenMqtt(license.tokenMqtt || license.token_mqtt || "");
      setMetadata(license.metadata || {});
      setIsStartPaid(license.startedPaid || false);
      setDiscountOnAutomaticReadjustment(license.discountOnAutomaticReadjustment);

      setStartDate(
         license.startDate
            ? license.startDate.substring(0, 10)
            : license.start_date || ""
      );

      setInitialDueDate(
         license.initialDueDate
            ? license.initialDueDate.substring(0, 10)
            : license.initial_due_date || ""
      );

      setDescription(license.description || "");
      setAutomaticRenew(license.automaticRenew ?? false);
      setType(license.type || "");
      setTypeCode(license.type?.code || "");
      setFeatures(license.features || []);
      setFeatureCodes(license.features?.map((feature) => feature.code) || []);
      setPeriod(license.period || "");
      setAmount(license.amount ?? 0);

      setDiscountReason(license.discount?.reason || null);

      setShowLoader(false);
   }, [props.license]);

   const buildPayload = () => {
      const payload = {
         tag,
         bindingCode,
         initialOwnerUnitId,
         organizationId,
         serialNumber,
         tokenMqtt,
         startDate: new Date(
            `${startDate}T00:00:00`
         ).toISOString(),

         initialDueDate: new Date(
            `${initialDueDate}T00:00:00`
         ).toISOString(),
         description,
         automaticRenew,
         automaticReadjustment,
         type: typeCode,
         features: featureCodes,
         metadata,
         period,
         amount: Number(amount),
         isStartPaid,
         discountOnReadjustment: discountOnAutomaticReadjustment,
         paymentMethod,
         discount: (Number(amount) !== totalAmount && discountReason) ? {
            type: DiscountType.Percentage,
            reason: discountReason,
         } : null,
      };

      if (discountReason?.trim()) {
         payload.discount = {
            reason: discountReason.trim(),
         };
      }

      return payload;
   };

   async function recalculateTotalAmount() {
      if (!typeCode || !period) {
         setTotalAmount(0);
         return;
      }

      setIsRecalculatingTotalAmount(true);
      try {
         const response = await LicenseService.calculateTotalAmount(
            period,
            typeCode,
            featureCodes
         );

         setTotalAmount(response.data.totalAmount);
      } catch (error) {
         console.error("Erro ao calcular o valor total:", error);
         setTotalAmount(0);
      } finally {
         setIsRecalculatingTotalAmount(false);
      }
   }

   useEffect(() => {
      recalculateTotalAmount();
   }, [
      typeCode,
      featureCodes,
      period
   ]);

   const validateForm = () => {
      const result = licenseSchema.safeParse({
         tag,
         bindingCode,
         initialOwnerUnitId,
         organizationId,
         serialNumber,
         tokenMqtt,
         startDate,
         initialDueDate,
         description,
         automaticRenew,
         automaticReadjustment,
         type: typeCode,
         features: featureCodes,
         period,
         isStartPaid,
         discountOnAutomaticReadjustment,
         paymentMethod,
         amount:
            amount !== "" &&
               amount !== null &&
               amount !== undefined
               ? Number(amount)
               : undefined,
         predictedAmount:
            totalAmount !== "" &&
               totalAmount !== null &&
               totalAmount !== undefined
               ? Number(totalAmount)
               : undefined,
         metadata,
         discountReason,
      });

      if (!result.success) {
         const fieldErrors = {};

         result.error.issues.forEach((issue) => {
            const field = issue.path[0];

            if (field && !fieldErrors[field]) {
               fieldErrors[field] = issue.message;
            }
         });

         console.log(fieldErrors);

         setErrors(fieldErrors);

         return false;
      }

      setErrors({});

      return true;
   };

   async function handleSubmit(event) {
      event.preventDefault();

      if (props.type === "consulta") {
         return;
      }

      const isValid = validateForm();

      if (!isValid) {
         return;
      }

      setIsSubmitting(true);

      try {
         const payload = buildPayload();

         if (props.type === "edicao") {
            await handleEdit(payload);
            return;
         }

         const response =
            await LicenseService.createLicense(payload);

         if (response.data.id) {
            navigate("../licenses");
         }
      } finally {
         setIsSubmitting(false);
      }
   }

   async function handleEdit(payload) {
      const response = await ClientLicenceService.updateClientLicence(
         props.license.id,
         payload
      );

      if (response.data.id) {
         navigate("../licenses");
      }
   }

   async function handleDelete() {
      if (!props.license) return;

      await ClientLicenceService.deleteClientLicence(props.license.id);

      navigate("../licenses", {
         state: { deletedId: props.license.id },
      });
   }

   const handleClickEditButton = () => {
      if (!props.license) return;

      navigate("/cadastro-licenses", {
         state: props.license,
      });
   };

   const handleNewToken = (newToken) => {
      if (tokenMqtt !== "" && props.license?.tokenMqtt) {
         setShowModalConfirmNewToken(true);
      }

      setTokenMqtt(newToken);
   };

   const closeModalConfirmNewToken = (action) => {
      setShowModalConfirmNewToken(false);

      if (action === "cancel") {
         setTokenMqtt(
            props.license?.tokenMqtt ||
            props.license?.token_mqtt ||
            ""
         );
      }
   };

   const handleFeaturesChange = ({ target }) => {
      const value = target.value;

      setFeatures(
         value
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean)
      );
   };

   return (
      <React.Fragment>
         <FormTitle
            type={props.type}
            tituloConsulta="Licenças"
            tituloCadastro="Cadastro de Licença"
            icon={<LuFileBadge size={20} />}
         />

         {showLoader ? (
            <Loader />
         ) : (
            <Form
               handleSubmit={handleSubmit}
               type={props.type}
               handleClickEdit={handleClickEditButton}
               handleDelete={handleDelete}
               navigateCancelUrl={"../licenses"}
               isSubmitting={isSubmitting}
            >
               <div className="flex flex-col w-full gap-3!">
                  <div className="w-full flex-none! grid grid-cols-2 gap-3!">
                     <Input
                        name="tag"
                        id="tag"
                        labelText="Nome *"
                        value={tag}
                        onChange={({ target }) => setTag(target.value)}
                        disabled={props.type === "consulta"}
                        error={errors.tag}
                     />
                     <div className="w-full [&>div]:w-full!">
                        <TokenGenerator
                           name="tokenMqtt"
                           id="tokenMqtt"
                           value={tokenMqtt}
                           disabled={props.type === "consulta"}
                           handleNewToken={handleNewToken}
                           error={errors.tokenMqtt}
                        />
                     </div>
                  </div>

                  <div className="w-full">
                     <TextAreaInput
                        name="description"
                        id="description"
                        labelText="Descrição"
                        type={"textfield"}
                        value={description}
                        onChange={({ target }) => setDescription(target.value)}
                        error={errors.description}
                     />
                  </div>

                  <div className="w-full flex-none! grid grid-cols-2 gap-3!">
                     <OrganizationSelector
                        value={organizationId}
                        onChange={setOrganizationId}
                        required={true}
                        error={errors.organizationId}
                     />

                     <UnitSelector
                        type={props.type}
                        fetch={true}
                        clientId={organizationId}
                        parentId={initialOwnerUnitId}
                        setParentId={setInitialOwnerUnitId}
                        modalTitle="Selecione a unidade"
                        inputLabel="Unidade proprietária"
                        disabled={props.type !== "cadastro" || isSubmitting}
                        error={errors.initialOwnerUnitId}
                     />
                  </div>

                  <ExpandableSection title="Configuração de licença" className="w-full flex flex-col gap-3" isExpanded={true}>
                     <div className="w-full flex-none! grid grid-cols-2 gap-3!">
                        <LicenseTypeSelector
                           value={typeCode}
                           onChange={e => setTypeCode(e.target.value)}
                           type={props.type}
                           isSubmitting={isSubmitting}
                           error={errors.type}
                        />

                        <Input
                           name="bindingCode"
                           id="bindingCode"
                           labelText="Código de Vinculação"
                           value={bindingCode}
                           onChange={({ target }) =>
                              setBindingCode(target.value)
                           }
                           disabled={props.type === "consulta"}
                           error={errors.bindingCode}
                        />
                     </div>

                     <div className="w-full">
                        <LicenseFeaturesSelector
                           typeCode={typeCode}
                           values={featureCodes}
                           onChange={setFeatureCodes}
                           type={props.type}
                           isSubmitting={isSubmitting}
                           error={errors.features}
                        />
                     </div>
                  </ExpandableSection>

                  {featureCodes?.includes("F-B") && (
                     <ExpandableSection title="Configuração de backup" className="w-full flex flex-col gap-3" isExpanded={true}>
                        <ToggleButton
                           label={`${metadata.enabledBackup ? "Backup Habilitado" : "Backup Desabilitado"
                              }`}
                           selected={metadata.enabledBackup}
                           onClick={() => setMetadata({ ...metadata, enabledBackup: !metadata.enabledBackup })}
                           disabled={props.type === "consulta"}
                           tooltipVisible={true}
                           tooltipText={
                              "Habilita ou desabilita o backup, quando habilitado o sistema irá realizar backup dos dados do equipamento para o cliente."
                           }
                        />
                        <ToggleButton
                           label={`${metadata.enabledRotation ? "Rotação de Backup Habilitada" : "Rotação de Backup Desabilitada"
                              }`}
                           selected={metadata.enabledRotation}
                           onClick={() => setMetadata({ ...metadata, enabledRotation: !metadata.enabledRotation })}
                           disabled={props.type === "consulta" || !metadata.enabledBackup}
                           tooltipVisible={true}
                           tooltipText={
                              "Habilita ou desabilita a rotação de backup, quando habilitado, quando o espaço para um novo backup não for suficiente, o mais antigo é removido."
                           }
                        />
                        <div className="w-full mt-2!">
                           <Input
                              name={"backupSize"}
                              id="backupSize"
                              type="number"
                              labelText={
                                 metadata.enabledBackup
                                    ? "Tamanho máximo para backup (Bytes) *"
                                    : "Tamanho máximo para backup (Bytes)"
                              }
                              value={metadata.backupSize}
                              onChange={({ target }) => setMetadata({ ...metadata, backupSize: Number(target.value) })}
                              disabled={props.type === "consulta" || !metadata.enabledBackup}
                              required={metadata.enabledBackup}
                              tooltipVisible={true}
                              tooltipText={
                                 "Tamanho máximo para backup, o sistema irá realizar backup dos dados do equipamento para o cliente."
                              }
                              error={errors.backupSize}
                           />
                        </div>
                     </ExpandableSection>
                  )}

                  <ExpandableSection title="Configuração de cobrança" className="w-full flex flex-col gap-3" isExpanded={true}>
                     <div className="w-full flex-none! grid grid-cols-2 gap-3!">
                        <BillingPeriodSelector
                           value={period}
                           onChange={({ target }) =>
                              setPeriod(target.value)
                           }
                           isSubmitting={isSubmitting}
                           error={errors.period}
                        />

                        <div className="w-full flex-none! grid grid-cols-2 gap-3!">
                           <PriceInput
                              name="amount"
                              id="amount"
                              labelText={'Valor'}
                              value={formatMoney(amount ?? 0)}
                              onChange={({ target }) =>
                                 setAmount(Number(target.value) || 0)
                              }
                              error={errors.amount}
                           />
                           <div className="w-full mt-5! bg-(--borderColor)/40! rounded-(--borderRadius)! box-border! px-3! py-2! text-(--textColor) flex items-center justify-between gap-3!">
                              <span>Total previsto: </span>
                              <b className="font-semibold!">
                                 {isRecalculatingTotalAmount ? (
                                    <>Recalculando...</>
                                 ) : (
                                    <>
                                       R$ {formatMoney(totalAmount)}
                                       {amount !== totalAmount && (
                                          <span className={`${totalAmount - amount < 0 ? 'text-green-500' : 'text-red-400'} ml-2! text-xs!`}>
                                             ({totalAmount - amount < 0 ? '+' : '-'}{formatMoney(Math.abs(totalAmount - amount))})
                                          </span>
                                       )}
                                    </>
                                 )}
                              </b>
                           </div>
                        </div>

                        <Input
                           name="startDate"
                           id="startDate"
                           type="date"
                           labelText="Data de Início *"
                           value={startDate}
                           onChange={({ target }) =>
                              setStartDate(target.value)
                           }
                           disabled={props.type === "consulta"}
                           error={errors.startDate}
                        />

                        <Input
                           name="initialDueDate"
                           id="initialDueDate"
                           type="date"
                           labelText="Data de Vencimento Inicial *"
                           value={initialDueDate}
                           onChange={({ target }) =>
                              setInitialDueDate(target.value)
                           }
                           disabled={props.type === "consulta"}
                           error={errors.initialDueDate}
                        />

                        {amount !== totalAmount && (
                           <div className="col-span-2">
                              <DiscountReasonSelector
                                 value={discountReason}
                                 setValue={setDiscountReason}
                                 type={props.type}
                                 error={errors.discountReason}
                                 disabled={props.type === "consulta"}
                              />
                           </div>
                        )}
                     </div>

                     <div className="flex flex-col gap-3 w-full mt-2!">
                        <div className="w-full grid grid-cols-2 gap-3">
                           <div className={`flex justify-start ${isStartPaid ? 'col-span-1' : 'col-span-2'}`}>
                              <ToggleButton
                                 label={`Iniciar pago`}
                                 selected={isStartPaid}
                                 onClick={() =>
                                    setIsStartPaid(!isStartPaid)
                                 }
                                 disabled={props.type === "consulta"}
                                 tooltipVisible={true}
                                 tooltipText="Define se a licença iniciará com a primeira cobrança paga."
                              />
                           </div>
                           {isStartPaid && (
                              <div className="col-span-1">
                                 <Select
                                    labelText="Forma de pagamento *"
                                    value={paymentMethod}
                                    onChange={({ target }) => {
                                       setPaymentMethod(target.value);
                                    }}
                                    disabled={props.type === "consulta" || isSubmitting}
                                    options={Object.entries(PaymentMethodText)?.map(([method, label]) => ({
                                       value: method,
                                       text: label,
                                    }))}
                                    error={errors.paymentMethod}
                                 />
                              </div>
                           )}
                        </div>
                        <ToggleButton
                           label={
                              automaticRenew
                                 ? "Renovação Automática Habilitada"
                                 : "Renovação Automática Desabilitada"
                           }
                           selected={automaticRenew}
                           onClick={() =>
                              setAutomaticRenew(!automaticRenew)
                           }
                           disabled={props.type === "consulta"}
                           tooltipVisible={true}
                           tooltipText="Define se a licença será renovada automaticamente ao atingir a data de vencimento."
                        />
                        {automaticRenew && (
                           <div className="w-full grid grid-cols-2 gap-3">
                              <div className={`flex justify-start ${automaticReadjustment ? 'col-span-1' : 'col-span-2'}`}>
                                 <ToggleButton
                                    label={
                                       automaticReadjustment
                                          ? "Reajuste Automático de Preços Habilitado"
                                          : "Reajuste Automático de Preços Desabilitado"
                                    }
                                    selected={automaticReadjustment}
                                    onClick={() =>
                                       setAutomaticReadjustment(!automaticReadjustment)
                                    }
                                    disabled={props.type === "consulta"}
                                    tooltipVisible={true}
                                    tooltipText="Define se o preço total da licença será reajustado automaticamente quando o tipo e/ou funcionalidades forem reajustados."
                                 />
                              </div>
                              {automaticReadjustment && (
                                 <div className="col-span-1">
                                    <Select
                                       labelText="Desconto no reajuste *"
                                       value={discountOnAutomaticReadjustment}
                                       onChange={({ target }) => {
                                          setDiscountOnAutomaticReadjustment(target.value);
                                       }}
                                       disabled={props.type === "consulta" || isSubmitting}
                                       options={Object.entries(DiscountOnAutomaticReadjustmentText)?.map(([method, label]) => ({
                                          value: method,
                                          text: label,
                                       }))}
                                       tooltipVisible={true}
                                       tooltipText="Defina o que deve acontecer com o desconto nas próximas cobranças automáticas."
                                       error={errors.discountOnAutomaticReadjustment}
                                    />
                                 </div>
                              )}
                           </div>
                        )}
                     </div>
                  </ExpandableSection>

                  <ModalComponent
                     modalIsOpen={showModalConfirmNewToken}
                     handleConfirm={() =>
                        closeModalConfirmNewToken()
                     }
                     handleCancel={() =>
                        closeModalConfirmNewToken("cancel")
                     }
                     icon={<IoWarningOutline size={30} />}
                     modalText="Confirmar operação? A conexão com o equipamento será perdida!"
                  />
               </div>
            </Form>
         )}
      </React.Fragment>
   );
}

export default FormLicense;
